import { createContext, useContext, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Tabs } from 'antd'
import type { WorkflowSnapshot } from 'shared'
import { OrderNodeOutlet } from '@/components/order-node-actions'
import { TOOLBAR_END_NODE, TOOLBAR_NODE } from '@/lib/order-actions'

/**
 * 统一订单详情布局：所有角色共用同一结构，各适配器只决定显示哪些区块、字段和按钮。
 * - 头部（渲染进弹窗标题栏，始终可见）：订单号、类型、状态、公司、金额摘要
 * - 主区：订单资料等页签，打开即看订单详情
 * - 侧栏：订单进度与推进流程的按钮
 * - 底部固定栏：不推进流程的订单操作（导出、改资料、改价、异常、取消等）
 */
interface OrderDetailChrome { summaryHost: HTMLElement | null }
const ChromeContext = createContext<OrderDetailChrome>({ summaryHost: null })
export const OrderDetailChromeProvider = ChromeContext.Provider

export const ORDER_DETAIL_TABS = ['订单资料', '设备与检测', '证书与交付', '单据与收付款', '处理记录'] as const
export type OrderDetailTabKey = typeof ORDER_DETAIL_TABS[number]
export interface OrderDetailTab { key: OrderDetailTabKey; content: ReactNode }

export function OrderDetailLayout({ summary, tabs, side, toolbar, toolbarEnd }: {
  summary: ReactNode
  tabs: OrderDetailTab[]
  side: ReactNode
  /** Direct bottom-bar content for adapters without portal actions (e.g. check view). */
  toolbar?: ReactNode
  toolbarEnd?: ReactNode
}) {
  const { summaryHost } = useContext(ChromeContext)
  const [active, setActive] = useState<string>(tabs[0]?.key || ORDER_DETAIL_TABS[0])
  const hideEmptyToolbar = !toolbar && !toolbarEnd
  return (
    <div className="order-detail-layout flex min-h-0 flex-1 flex-col">
      {summaryHost ? createPortal(summary, summaryHost) : summary}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto lg:flex-row lg:overflow-hidden">
        <main aria-label="订单资料" className="order-detail-main min-w-0 lg:flex-1 lg:overflow-y-auto">
          <Tabs activeKey={active} onChange={setActive}
            items={tabs.map(tab => ({ key: tab.key, label: tab.key, children: <div className="order-detail-pane space-y-4 px-5 pb-5">{tab.content}</div> }))} />
        </main>
        <aside aria-label="订单进度" className="order-detail-side shrink-0 border-t border-slate-100 bg-slate-50/70 p-4 lg:w-[360px] lg:overflow-y-auto lg:border-l lg:border-t-0">
          {side}
        </aside>
      </div>
      <footer aria-label="订单操作" className={`${hideEmptyToolbar ? 'order-detail-toolbar-auto ' : ''}flex shrink-0 flex-wrap items-center gap-2 border-t border-slate-200 bg-white px-5 py-2.5`}>
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <OrderNodeOutlet node={TOOLBAR_NODE} className="contents" />
          {toolbar}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <OrderNodeOutlet node={TOOLBAR_END_NODE} className="contents" />
          {toolbarEnd}
        </div>
      </footer>
    </div>
  )
}

/** Loading / error body inside the fixed-size detail frame. */
export function OrderDetailState({ children }: { children: ReactNode }) {
  return <div className="flex min-h-0 flex-1 items-center justify-center p-8">{children}</div>
}

export type SummaryTone = 'sky' | 'amber' | 'violet' | 'blue' | 'emerald' | 'rose' | 'slate'
const TONES: Record<SummaryTone, string> = {
  sky: 'bg-sky-50 text-sky-700 ring-sky-200/70', amber: 'bg-amber-50 text-amber-700 ring-amber-200/70',
  violet: 'bg-violet-50 text-violet-700 ring-violet-200/70', blue: 'bg-blue-50 text-blue-700 ring-blue-200/70',
  emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200/70', rose: 'bg-rose-50 text-rose-700 ring-rose-200/70',
  slate: 'bg-slate-100 text-slate-600 ring-slate-200',
}
export function SummaryTag({ tone = 'slate', children }: { tone?: SummaryTone; children: ReactNode }) {
  return <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${TONES[tone]}`}>{children}</span>
}

/** 头部摘要：左侧订单身份与状态，右侧金额（应收及到账/结清情况）。 */
export function OrderDetailSummary({ orderNo, tags, meta, amount }: {
  orderNo: ReactNode; tags?: ReactNode; meta?: ReactNode[]; amount?: ReactNode
}) {
  return (
    <div className="flex min-w-0 flex-wrap items-end justify-between gap-x-6 gap-y-1">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-base font-semibold tracking-tight text-slate-900">{orderNo}</span>
          {tags}
        </div>
        {!!meta?.filter(Boolean).length && <div className="mt-1 flex flex-wrap items-center gap-x-2 text-[13px] text-slate-500">
          {meta.filter(Boolean).map((item, index) => <span key={index} className="flex items-center gap-2">{index > 0 && <span className="text-slate-300">·</span>}{item}</span>)}
        </div>}
      </div>
      {amount}
    </div>
  )
}

export function OrderAmountSummary({ label, value, detail }: { label: ReactNode; value: ReactNode; detail?: ReactNode }) {
  return (
    <div className="text-right">
      <div className="flex items-baseline justify-end gap-2">
        <span className="text-xs text-slate-400">{label}</span>
        <span className="text-xl font-semibold tabular-nums tracking-tight text-slate-900">{value}</span>
      </div>
      {detail && <div className="mt-0.5 text-xs text-slate-500">{detail}</div>}
    </div>
  )
}

/** 工作流已确认的收款口径，原位于进度卡片底部，现统一在头部金额区显示。 */
export function WorkflowAmountLine({ workflow }: { workflow?: WorkflowSnapshot }) {
  if (!workflow) return null
  const money = (value: string) => `¥${Number(value).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`
  const { amounts } = workflow
  return <span className="tabular-nums">
    已确认收款 {money(amounts.confirmedReceived)} · 已结清 {money(amounts.settled)} · 未结清 {money(amounts.outstanding)}
    {Number(amounts.refunded || 0) > 0 && <> · 已退费 {money(amounts.refunded!)}（应收按退费后计）</>}
  </span>
}
